import React, {useState} from 'react'
import {useAppSelector} from '../../store/hooks'
import {selectUser, setUser} from '../../store/userSlice'
import LoadingSpinner, {LoadingSpinnerSize} from '../common/LoadingSpinner'
import {Button, Input, Modal} from 'react-daisyui'
import ResetPassword from './ResetPassword'
import VerifyUserAttribute from './VerifyUserAttribute'
import NotificationPreferences from './NotificationPreferences'
import {useForm} from 'react-hook-form'
import {updateUserAttributes, fetchUserAttributes} from 'aws-amplify/auth'
import PhoneInputWithCountry from 'react-phone-number-input/react-hook-form'
import {UserRole} from '../../types'
import {trackProfileEdit, trackFormSubmission} from '../../services/analytics'
import {trackClick} from '../../services/coreAnalytics'

interface UserAttributes {
  family_name?: string
  given_name?: string
  email?: string
  phone_number?: string
}

export const EditProfile = () => {
  const user = useAppSelector(selectUser)
  const [isUpdating, setIsUpdating] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: {errors, isDirty},
    watch
  } = useForm<UserAttributes>({
    defaultValues: {
      given_name: user.firstName,
      family_name: user.lastName,
      email: user.email,
      phone_number: user.phoneNumber
    }
  })

  const [showVerifyPhone, setShowVerifyPhone] = useState(false)
  const [showVerifyEmail, setShowVerifyEmail] = useState(false)

  const [verification, setVerification] = useState({
    emailVerified: user.emailVerified,
    phoneVerified: user.phoneNumberVerified
  })

  const [editting, setEditting] = useState(false)

  const handleEditProfile = async (data: any) => {
    setEditting(false)
    setIsUpdating(true)

    // Track form submission start
    trackFormSubmission('profile_edit', 'form_submitted', undefined, user.email)

    try {
      await updateUserAttributes({userAttributes: data})
      const refreshedUser = await fetchUserAttributes()

      const updatedForm = {
        given_name: refreshedUser.given_name.toString(),
        family_name: refreshedUser.family_name.toString(),
        email: refreshedUser.email.toString(),
        phone_number: refreshedUser.phone_number.toString()
      }

      reset(updatedForm)

      setVerification({
        phoneVerified:
          (refreshedUser.phone_number_verified as unknown) === 'true',
        emailVerified: (refreshedUser.email_verified as unknown) === 'true'
      })

      // Track successful profile save
      trackProfileEdit('save_profile', undefined, user.email)
      trackFormSubmission('profile_edit', 'form_success', undefined, user.email)
    } catch (error) {
      setErrorMessage(error.message)
      // Track form error
      trackFormSubmission(
        'profile_edit',
        'form_error',
        [error.message],
        user.email
      )
    } finally {
      setIsUpdating(false)
    }
  }

  const {Dialog} = Modal.useDialog()

  const [showResetPwdDialog, setShowResetPwdDialog] = useState(false)

  const handleCancelEdit = () => {
    // Track cancel edit
    trackProfileEdit('cancel_edit', undefined, user.email)
    trackFormSubmission('profile_edit', 'form_cancelled', undefined, user.email)

    reset()
    setEditting(false)
  }

  const onPasswordResetSuccess = () => {
    setShowResetPwdDialog(false)
  }

  return (
    <div className="container px-4 md:px-10">
      <Dialog
        open={showVerifyPhone}
        onClose={() => setShowVerifyPhone(false)}
        className="modal-box bg-white max-w-sm"
        aria-labelledby="verify-phone-title"
        aria-describedby="verify-phone-description"
      >
        <h2 id="verify-phone-title" className="sr-only">
          Verify Phone Number
        </h2>
        <p id="verify-phone-description" className="sr-only">
          Verify your phone number to complete your profile.
        </p>
        <VerifyUserAttribute
          attributeName="Phone Number"
          attribute="phone_number"
          attributeValue={watch('phone_number')}
          onVerifySuccess={() => {
            setVerification({
              ...verification,
              phoneVerified: true
            })
            setShowVerifyPhone(false)
          }}
        ></VerifyUserAttribute>
        <Modal.Actions></Modal.Actions>
      </Dialog>
      <Dialog
        open={showVerifyEmail}
        onClose={() => setShowVerifyEmail(false)}
        className="modal-box bg-white max-w-sm"
        aria-labelledby="verify-email-title"
        aria-describedby="verify-email-description"
      >
        <h2 id="verify-email-title" className="sr-only">
          Verify Email
        </h2>
        <p id="verify-email-description" className="sr-only">
          Verify your email address to complete your profile.
        </p>
        <VerifyUserAttribute
          attributeName="Email"
          attribute="email"
          attributeValue={watch('email')}
        ></VerifyUserAttribute>
      </Dialog>
      <Dialog
        open={showResetPwdDialog}
        onClose={() => setShowResetPwdDialog(false)}
        className="modal-box bg-white max-w-sm"
        aria-labelledby="reset-password-title"
        aria-describedby="reset-password-description"
      >
        <h2 id="reset-password-title" className="sr-only">
          Reset Password
        </h2>
        <p id="reset-password-description" className="sr-only">
          Reset your password to secure your account.
        </p>
        <ResetPassword onSuccess={onPasswordResetSuccess}></ResetPassword>
        <form method="dialog">
          <Button className="btn btn-link no-underline text-sm w-full">
            cancel
          </Button>
        </form>
      </Dialog>
      <div className="grid grid-flow-row gap-4">
        <h1 className="text-xl font-semibold">Edit my info</h1>
        <p className="text-sm font-light">
          Update your profile information here.
        </p>
        <form
          onSubmit={handleSubmit(handleEditProfile)}
          onReset={handleCancelEdit}
          noValidate
          aria-labelledby="edit-profile-form-title"
        >
          <h2 id="edit-profile-form-title" className="sr-only">
            Edit Profile Form
          </h2>
          <div className="form-control grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
            <div className="col-span-2 sm:col-span-1">
              <label className="label pt-2 pb-1" htmlFor="first_name">
                <span
                  className={
                    'label-text' + (errors.given_name ? ' text-red-error' : '')
                  }
                >
                  First Name
                </span>
              </label>
              <Input
                id="first_name"
                name="first_name"
                className={
                  'input-form' + (errors.given_name ? ' input-form-error' : '')
                }
                required
                aria-required="true"
                aria-invalid={!!errors.given_name}
                {...register('given_name', {required: true})}
              />
              {errors.given_name?.type === 'required' && (
                <p className="text-red-error" role="alert" aria-live="polite">
                  First name is required
                </p>
              )}
            </div>

            <div className="col-span-2 sm:col-span-1">
              <label className="label pb-1" htmlFor="family_name">
                <span
                  className={
                    'label-text' + (errors.family_name ? ' text-red-error' : '')
                  }
                >
                  Last Name
                </span>
              </label>
              <Input
                id="family_name"
                name="family_name"
                required
                aria-required="true"
                aria-invalid={!!errors.family_name}
                className={
                  'input-form' + (errors.family_name ? ' input-form-error' : '')
                }
                {...register('family_name', {required: true})}
              />
              {errors.family_name?.type === 'required' && (
                <p className="text-red-error" role="alert" aria-live="polite">
                  Last name is required
                </p>
              )}
            </div>

            <div className="col-span-2 sm:col-span-1">
              <label className="label pb-1" htmlFor="phone_number">
                <span className="label-text">Phone Number</span>
                <span className="label-text-alt  text-cabana">
                  {!isDirty && (
                    <div>
                      {verification.phoneVerified ? (
                        'Verified'
                      ) : (
                        <div
                          className="btn btn-link btn-xs no-underline text-xs text-red-800"
                          onClick={() => {
                            // Track verify phone click
                            trackClick(
                              'verify-phone-button',
                              '/account/profile',
                              user.email
                            )
                            trackProfileEdit(
                              'edit_profile',
                              'phone',
                              user.email
                            )

                            setShowVerifyPhone(true)
                          }}
                          role="button"
                          aria-label="Verify phone number"
                        >
                          Verify
                        </div>
                      )}
                    </div>
                  )}
                </span>
              </label>
              <PhoneInputWithCountry
                id="phone_number"
                placeholder="Enter phone number"
                defaultCountry="US"
                name="phone_number"
                control={control}
                className="PhoneInput"
                aria-label="Phone number input"
              />
            </div>

            <div className="col-span-2 sm:col-span-1">
              <label className="label pb-1" htmlFor="email">
                <span
                  className={
                    'label-text' + (errors.email ? ' text-red-error' : '')
                  }
                >
                  Email
                </span>
                <span className="label-text-alt text-cabana">
                  {!isDirty && (
                    <div>
                      {verification.emailVerified ? (
                        'Verified'
                      ) : (
                        <div
                          className="btn btn-xs btn-link no-underline text-sm text-red-800"
                          onClick={() => {
                            // Track verify email click
                            trackClick(
                              'verify-email-button',
                              '/account/profile',
                              user.email
                            )
                            trackProfileEdit(
                              'edit_profile',
                              'email',
                              user.email
                            )

                            setShowVerifyEmail(true)
                          }}
                          role="button"
                          aria-label="Verify email address"
                        >
                          Verify
                        </div>
                      )}
                    </div>
                  )}
                </span>
              </label>
              <Input
                id="email"
                name="email"
                required
                type="email"
                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$"
                className={
                  'input-form' + (errors.email ? ' input-form-error' : '')
                }
                disabled={true}
                aria-required="true"
                aria-invalid={!!errors.email}
                {...register('email', {required: true})}
              />
              {errors.email?.type === 'required' && (
                <p className="text-red-error" role="alert" aria-live="polite">
                  Email is required
                </p>
              )}
            </div>

            <div className="col-span-2 sm:col-span-1">
              <label className="label pt-6 pb-1" htmlFor="password">
                <span className="label-text">Password</span>
              </label>
              <Input
                id="password"
                name="password"
                type="password"
                disabled={true}
                value={'********'}
                required
                className={'input-form'}
                aria-label="Password input"
              />
              <div className="pt-4 cursor-pointer sm:col-span-2 sm:col-span-1">
                <a
                  className="text-cabana"
                  onClick={e => {
                    setShowResetPwdDialog(true)
                  }}
                  role="button"
                  aria-label="Reset password"
                >
                  Reset Password
                </a>
              </div>
            </div>
            <div></div>
            <div className="col-span-2">
              {isUpdating ? (
                <LoadingSpinner size={LoadingSpinnerSize.Large} />
              ) : (
                ''
              )}
              {isDirty ? (
                <div>
                  <Button
                    className="btn btn-link no-underline mx-2"
                    type="reset"
                    onClick={e => handleCancelEdit()}
                    aria-label="Cancel editing"
                  >
                    cancel
                  </Button>
                  <Button
                    className="btn btn-secondary"
                    type="submit"
                    aria-label="Save changes"
                  >
                    Save
                  </Button>
                </div>
              ) : (
                ''
              )}
            </div>
          </div>
        </form>

        <NotificationPreferences />
      </div>
    </div>
  )
}
